import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Users } from "lucide-react";
import { getRegions } from "@/lib/api";
import { Ornament } from "@/components/Ornament";

const CX = 340;
const CY = 340;
const R = 252;

function pos(angle) {
  const rad = ((angle - 90) * Math.PI) / 180;
  return { x: CX + R * Math.cos(rad), y: CY + R * Math.sin(rad) };
}

export default function MapPage() {
  const [regions, setRegions] = useState([]);
  const [active, setActive] = useState(null);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    getRegions().then(setRegions).catch(() => {});
  }, []);

  const activeIndex = active ? regions.findIndex((r) => r.id === active.id) : -1;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center max-w-2xl mx-auto">
        <span className="eyebrow">Dairevi Dünya Haritası</span>
        <h1 className="mt-3 font-[Cinzel] text-4xl sm:text-5xl font-bold text-foreground">
          Kaşgarlı'nın Dünyası
        </h1>
        <p className="mt-4 text-foreground/75">
          Kaşgarlı Mahmud, eserine Balasagun merkezli dairesel bir dünya haritası çizdi ve Türk
          boylarını batıdan doğuya sıraladı. Divân'da anılan <strong>20 Türk boyunu</strong> harita
          üzerindeki numaralardan ya da aşağıdaki galeriden keşfedin.
        </p>
        <div className="flex justify-center text-accent mt-6">
          <Ornament className="w-40 h-5" />
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-8 items-start">
        {/* Map */}
        <div className="lg:col-span-7">
          <div className="folio-frame rounded-lg bg-card p-4 sm:p-6">
            <svg
              viewBox="0 0 680 680"
              className="w-full h-auto"
              data-testid="interactive-turkic-map"
              role="img"
              aria-label="Kaşgarlı Mahmud dairevi dünya haritası, 20 Türk boyu"
            >
              <defs>
                <radialGradient id="seaGrad" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="hsl(var(--secondary))" />
                  <stop offset="100%" stopColor="hsl(var(--muted))" />
                </radialGradient>
              </defs>

              <circle cx={CX} cy={CY} r={326} fill="none" stroke="hsl(var(--accent))" strokeWidth="2" opacity="0.5" />
              <circle cx={CX} cy={CY} r={320} fill="url(#seaGrad)" />
              <circle cx={CX} cy={CY} r={292} fill="hsl(var(--background))" stroke="hsl(var(--border))" strokeWidth="1" />

              {[
                { t: "DOĞU", x: 340, y: 30 },
                { t: "BATI", x: 340, y: 656 },
                { t: "KUZEY", x: 34, y: 344 },
                { t: "GÜNEY", x: 646, y: 344 },
              ].map((d) => (
                <text
                  key={d.t}
                  x={d.x}
                  y={d.y}
                  textAnchor="middle"
                  className="fill-[hsl(var(--muted-foreground))]"
                  style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, letterSpacing: 2 }}
                >
                  {d.t}
                </text>
              ))}

              {/* Spokes */}
              {regions.map((r) => {
                const p = pos(r.angle);
                return (
                  <line key={`l-${r.id}`} x1={CX} y1={CY} x2={p.x} y2={p.y}
                    stroke="hsl(var(--border))" strokeWidth="1" strokeDasharray="3 5" opacity="0.5" />
                );
              })}

              {/* Tribe nodes (numbered) */}
              {regions.map((r, i) => {
                const p = pos(r.angle);
                const isActive = active?.id === r.id;
                const isHover = hover === r.id;
                const big = isActive || isHover;
                return (
                  <g
                    key={r.id}
                    data-testid={`map-region-${r.id}`}
                    onClick={() => setActive(r)}
                    onMouseEnter={() => setHover(r.id)}
                    onMouseLeave={() => setHover(null)}
                    style={{ cursor: "pointer" }}
                  >
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={big ? 22 : 16}
                      fill={r.color}
                      opacity={isActive ? 1 : isHover ? 0.95 : 0.85}
                      stroke="hsl(var(--accent))"
                      strokeWidth={isActive ? 3 : 1.5}
                      style={{ transition: "r 0.18s ease, opacity 0.18s ease" }}
                    />
                    <text
                      x={p.x}
                      y={p.y + 4}
                      textAnchor="middle"
                      className="fill-white"
                      style={{ fontFamily: "'Cinzel', serif", fontSize: big ? 14 : 11, fontWeight: 700, pointerEvents: "none" }}
                    >
                      {i + 1}
                    </text>
                  </g>
                );
              })}

              {/* Hover / active name label */}
              {(() => {
                const r = regions.find((x) => x.id === (hover || active?.id));
                if (!r) return null;
                const p = pos(r.angle);
                const dirX = Math.cos(((r.angle - 90) * Math.PI) / 180);
                const anchor = dirX > 0.2 ? "start" : dirX < -0.2 ? "end" : "middle";
                const ox = p.x + dirX * 26;
                const oy = p.y + Math.sin(((r.angle - 90) * Math.PI) / 180) * 26 + 4;
                return (
                  <text
                    x={ox}
                    y={oy}
                    textAnchor={anchor}
                    className="fill-[hsl(var(--foreground))]"
                    style={{ fontFamily: "'Cinzel', serif", fontSize: 14, fontWeight: 700, pointerEvents: "none", paintOrder: "stroke" }}
                    stroke="hsl(var(--card))"
                    strokeWidth="4"
                  >
                    {r.name}
                  </text>
                );
              })()}

              {/* Center medallion: Balasagun */}
              <g style={{ pointerEvents: "none" }}>
                <circle cx={CX} cy={CY} r={56} fill="#C28B38" stroke="hsl(var(--primary))" strokeWidth="3" />
                <circle cx={CX} cy={CY} r={47} fill="none" stroke="white" strokeWidth="1" opacity="0.6" />
                <text x={CX} y={CY - 2} textAnchor="middle" className="fill-white"
                  style={{ fontFamily: "'Cinzel', serif", fontSize: 16, fontWeight: 700 }}>
                  Balasagun
                </text>
                <text x={CX} y={CY + 16} textAnchor="middle" className="fill-white"
                  style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, letterSpacing: 1.5, opacity: 0.85 }}>
                  MERKEZ
                </text>
              </g>
            </svg>
            <p className="text-center text-xs text-muted-foreground mt-2">
              Doğu yukarıda · Merkez: Balasagun · Numaralı boylara dokunun.
            </p>
          </div>
        </div>

        {/* Info panel */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <AnimatePresence mode="wait">
              {active ? (
                <motion.div
                  key={active.id}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.3 }}
                  data-testid="region-info-modal"
                  className="folio-frame rounded-lg bg-card p-7 sm:p-8"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="eyebrow flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5" /> {activeIndex + 1}. Boy · {active.direction}
                      </span>
                      <h2 className="mt-2 font-[Cinzel] text-3xl font-bold text-foreground">{active.name}</h2>
                    </div>
                    <button onClick={() => setActive(null)} aria-label="Kapat" className="text-muted-foreground hover:text-foreground">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="gold-rule my-5" />
                  {active.image && (
                    <div className="rounded-md overflow-hidden border border-border mb-4">
                      <img src={active.image} alt={active.name} loading="lazy" className="w-full h-auto block" />
                    </div>
                  )}
                  <p className="text-foreground/85 leading-relaxed">{active.description}</p>

                  <div className="mt-6">
                    <p className="eyebrow mb-2 flex items-center gap-2"><Users className="w-3.5 h-3.5" /> İlgili Adlar</p>
                    <div className="flex flex-wrap gap-2">
                      {active.tribes.map((t) => (
                        <span key={t} className="text-sm border border-border rounded-full px-3 py-1 bg-secondary/50 text-foreground/80">{t}</span>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5">
                    <p className="eyebrow mb-2">Ağızdan Kelimeler</p>
                    <div className="flex flex-wrap gap-2">
                      {active.words.map((w) => (
                        <span key={w} className="gokturk text-sm rubric border border-primary/30 rounded px-2.5 py-1">{w}</span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div key="placeholder" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="folio-frame rounded-lg bg-card p-10 text-center text-muted-foreground">
                  <MapPin className="w-8 h-8 mx-auto mb-4 text-accent" />
                  Haritadan numaralı bir boy seçerek yerleşimini, görselini ve diline dair notları görün.
                </motion.div>
              )}
            </AnimatePresence>

            {/* Numbered legend */}
            <div className="mt-5 grid grid-cols-2 gap-2 max-h-[40vh] overflow-y-auto pr-1">
              {regions.map((r, i) => (
                <button
                  key={r.id}
                  onClick={() => setActive(r)}
                  onMouseEnter={() => setHover(r.id)}
                  onMouseLeave={() => setHover(null)}
                  data-testid={`region-chip-${r.id}`}
                  className={`card-hover flex items-center gap-2 rounded-md border px-3 py-2 text-left ${
                    active?.id === r.id ? "border-accent ring-1 ring-accent/40" : "border-border"
                  } bg-card`}
                >
                  <span className="flex items-center justify-center w-5 h-5 rounded-full text-[0.6rem] font-bold text-white shrink-0" style={{ background: r.color }}>{i + 1}</span>
                  <span className="text-sm text-foreground/80 truncate">{r.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Türk Boyları gallery */}
      <section className="mt-16" data-testid="tribes-gallery">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <span className="eyebrow">Dîvân'ın Türk Dünyası</span>
          <h2 className="mt-3 font-[Cinzel] text-3xl sm:text-4xl font-bold text-foreground">20 Türk Boyu</h2>
          <p className="mt-3 text-foreground/70">Kaşgarlı'nın batıdan doğuya sıraladığı boyları görselleriyle tanıyın.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {regions.map((r, i) => (
            <button
              key={r.id}
              onClick={() => {
                setActive(r);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              data-testid={`tribe-card-${r.id}`}
              className="card-hover group relative rounded-lg overflow-hidden border border-border text-left"
            >
              {r.image && <img src={r.image} alt={r.name} loading="lazy" className="w-full aspect-square object-cover" />}
              <span className="absolute top-2 left-2 flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white border border-white/40" style={{ background: r.color }}>{i + 1}</span>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1F1510]/90 to-transparent p-3">
                <span className="block font-[Cinzel] text-sm text-[#F5EFEB] leading-tight">{r.name}</span>
                <span className="block text-[0.6rem] uppercase tracking-widest text-accent mt-0.5">{r.direction}</span>
              </div>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
